import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Search, Filter, X, ClipboardList, MapPin, Mail, Phone, Calendar, ShoppingBag, ExternalLink, Download, FileSpreadsheet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function SuperAdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [q, setQ] = useState("");
  const [filterNet, setFilterNet] = useState("all");
  const [filterTenant, setFilterTenant] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [filterMarketing, setFilterMarketing] = useState("all");
  const [sortBy, setSortBy] = useState("recent"); // recent | spent_desc | spent_asc | name
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/superadmin/customers").then((r) => setCustomers(r.data));
    api.get("/networks").then((r) => setNetworks(r.data));
    api.get("/superadmin/tenants").then((r) => setTenants(r.data)).catch(() => {});
  }, []);

  const openHistory = async (c) => {
    setHistoryFor(c);
    setHistory([]);
    const r = await api.get(`/superadmin/customers/${encodeURIComponent(c.email)}/orders`);
    setHistory(r.data || []);
  };

  // Derived filtered + sorted list
  const filtered = useMemo(() => {
    let arr = customers;
    if (q) {
      const ql = q.toLowerCase();
      arr = arr.filter((c) => [c.email, c.name, c.surname, c.full_name, c.phone, c.city, c.address, c.tenant_name]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(ql)));
    }
    if (filterNet !== "all") arr = arr.filter((c) => c.network_id === filterNet);
    if (filterTenant !== "all") arr = arr.filter((c) => c.tenant_id === filterTenant);
    if (filterSource !== "all") arr = arr.filter((c) => c.source === filterSource);
    if (filterMarketing !== "all") arr = arr.filter((c) => (!!c.consenso_marketing) === (filterMarketing === "yes"));
    if (sortBy === "spent_desc") arr = [...arr].sort((a, b) => (b.total_spent || 0) - (a.total_spent || 0));
    else if (sortBy === "spent_asc") arr = [...arr].sort((a, b) => (a.total_spent || 0) - (b.total_spent || 0));
    else if (sortBy === "name") arr = [...arr].sort((a, b) => String(a.surname || a.name || a.email).localeCompare(String(b.surname || b.name || b.email)));
    else arr = [...arr].sort((a, b) => String(b.last_order || "").localeCompare(String(a.last_order || "")));
    return arr;
  }, [customers, q, filterNet, filterTenant, filterSource, filterMarketing, sortBy]);

  const totalGmv = filtered.reduce((s, c) => s + (c.total_spent || 0), 0);
  const totalOrders = filtered.reduce((s, c) => s + (c.orders_count || 0), 0);

  const resetFilters = () => { setQ(""); setFilterNet("all"); setFilterTenant("all"); setFilterSource("all"); setFilterMarketing("all"); setSortBy("recent"); };

  const tenantsOfNet = filterNet === "all" ? tenants : tenants.filter((t) => t.network_id === filterNet);

  const downloadFile = async (fmt) => {
    try {
      const token = localStorage.getItem("tk_token");
      const r = await fetch(`${API}/superadmin/customers/export.${fmt}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(r.statusText);
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `clienti_centralizzati_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      link.click();
      toast.success(`Export ${fmt.toUpperCase()} pronto`);
    } catch (e) { toast.error("Errore export: " + e.message); }
  };

  return (
    <AdminLayout>
      <div className="p-8 lg:p-10" data-testid="sa-customers-page">
        <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="overline text-brand mb-2">SuperAdmin · Database centralizzato</div>
            <h1 className="font-heading font-bold text-4xl tracking-tight">Clienti centralizzati</h1>
            <p className="text-muted-foreground mt-2 text-sm">Vista cross-network di tutti i clienti finali della piattaforma. Filtra per network/origine, cerca e analizza lo storico ordini.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => downloadFile("xlsx")} data-testid="export-customers-xlsx"
              className="h-10 px-4 rounded-sm bg-foreground text-white text-sm font-medium hover:bg-brand transition-colors inline-flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4" /> Esporta XLSX
            </button>
            <button onClick={() => downloadFile("csv")} data-testid="export-customers-csv"
              className="h-10 px-4 rounded-sm border border-border text-sm hover:bg-accent inline-flex items-center gap-2">
              <Download className="w-4 h-4" /> Esporta CSV
            </button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <Kpi label="Clienti totali" value={filtered.length} testid="kpi-customers" />
          <Kpi label="Ordini totali" value={totalOrders} testid="kpi-orders" />
          <Kpi label="GMV cumulato" value={`€ ${totalGmv.toFixed(2)}`} testid="kpi-gmv" highlight />
          <Kpi label="Spesa media / cliente" value={filtered.length ? `€ ${(totalGmv / filtered.length).toFixed(2)}` : "€ 0"} testid="kpi-avg" />
        </div>

        {/* Filters bar */}
        <div className="bg-white border border-border rounded-sm p-3 mb-3 flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="cust-search"
              placeholder="Cerca per email, nome, cognome, telefono, città..." className="w-full h-10 pl-9 pr-3 rounded-sm border border-border text-sm" />
          </div>
          <Select value={filterNet} onValueChange={(v) => { setFilterNet(v); setFilterTenant("all"); }}>
            <SelectTrigger className="h-10 w-44 rounded-sm" data-testid="filter-net"><SelectValue placeholder="Network" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i network</SelectItem>
              {networks.map((n) => <SelectItem key={n.id} value={n.id}>{n.display_name || n.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterTenant} onValueChange={setFilterTenant}>
            <SelectTrigger className="h-10 w-48 rounded-sm" data-testid="filter-tenant"><SelectValue placeholder="Origine (tenant)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le origini</SelectItem>
              {tenantsOfNet.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.type})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterSource} onValueChange={setFilterSource}>
            <SelectTrigger className="h-10 w-36 rounded-sm" data-testid="filter-source"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="registered">Registrati</SelectItem>
              <SelectItem value="guest">Guest</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterMarketing} onValueChange={setFilterMarketing}>
            <SelectTrigger className="h-10 w-40 rounded-sm" data-testid="filter-marketing"><SelectValue placeholder="Marketing" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="yes">Marketing OK</SelectItem>
              <SelectItem value="no">Marketing NO</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-10 w-44 rounded-sm" data-testid="filter-sort"><SelectValue placeholder="Ordina" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Ordine più recente</SelectItem>
              <SelectItem value="spent_desc">Spesa ↓</SelectItem>
              <SelectItem value="spent_asc">Spesa ↑</SelectItem>
              <SelectItem value="name">Cognome A-Z</SelectItem>
            </SelectContent>
          </Select>
          {(q || filterNet !== "all" || filterTenant !== "all" || filterSource !== "all" || filterMarketing !== "all" || sortBy !== "recent") && (
            <button onClick={resetFilters} data-testid="filter-reset"
              className="h-10 px-3 rounded-sm border border-border text-xs hover:bg-accent inline-flex items-center gap-1.5">
              <X className="w-3.5 h-3.5" /> Reset
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white border border-border rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-border">
                  <Th>Cliente</Th>
                  <Th>Contatti</Th>
                  <Th>Indirizzo</Th>
                  <Th>Origine</Th>
                  <Th center>Ordini</Th>
                  <Th right>Speso</Th>
                  <Th>Ultimo ordine</Th>
                  <Th>Tipo</Th>
                  <Th right>Azioni</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.email + (c.id || "")} className="border-b border-border last:border-0 hover:bg-accent/30" data-testid={`customer-row-${c.email}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{[c.surname, c.name].filter(Boolean).join(" ") || c.full_name || "—"}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</div>
                      {c.consenso_marketing && <span className="inline-block mt-1 text-[9px] tracking-wider uppercase px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700">Marketing OK</span>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {c.phone ? <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</div> : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {c.address || c.city ? (
                        <div className="flex items-start gap-1">
                          <MapPin className="w-3 h-3 mt-1 flex-shrink-0" />
                          <div>
                            <div>{c.address || "—"}</div>
                            <div className="text-xs text-muted-foreground">{[c.cap, c.city, c.country].filter(Boolean).join(", ")}</div>
                          </div>
                        </div>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {c.tenant_name ? (
                        <div>
                          <div className="font-medium text-xs">{c.tenant_name}</div>
                          <div className="text-[10px] text-muted-foreground tracking-wider uppercase">{c.network_display}</div>
                        </div>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center font-medium">{c.orders_count || 0}</td>
                    <td className="px-4 py-3 text-right font-medium">€ {(c.total_spent || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm">{c.last_order ? new Date(c.last_order).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-[10px] tracking-wider uppercase rounded-sm ${c.source === "registered" ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-700"}`}>{c.source}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openHistory(c)} data-testid={`view-history-${c.email}`}
                        className="h-8 px-3 rounded-sm border border-border text-xs hover:bg-foreground hover:text-white inline-flex items-center gap-1.5 transition-colors">
                        <ClipboardList className="w-3.5 h-3.5" /> Storico
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="px-5 py-10 text-center text-sm text-muted-foreground">Nessun cliente trovato con i filtri correnti.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Order history modal */}
      {historyFor && (
        <Dialog open onOpenChange={() => setHistoryFor(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto" data-testid="customer-history-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" />
                Storico ordini · {[historyFor.surname, historyFor.name].filter(Boolean).join(" ") || historyFor.email}
              </DialogTitle>
              <div className="text-xs text-muted-foreground mt-1">
                <span>{historyFor.email}</span>
                {historyFor.phone && <> · {historyFor.phone}</>}
                {historyFor.city && <> · {historyFor.city}</>}
              </div>
            </DialogHeader>

            <div className="space-y-3 mt-4">
              {history.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-10">Nessun ordine</div>
              ) : history.map((o) => (
                <div key={o.id} className="border border-border rounded-sm p-3" data-testid={`order-${o.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="font-mono text-xs">{o.numero_ordine || o.id?.slice(0, 8)}</span>
                      <span className="ml-3 text-xs text-muted-foreground inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{o.created_at ? new Date(o.created_at).toLocaleString() : "—"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">€ {(o.totale || 0).toFixed(2)}</span>
                      <span className="text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-sm bg-blue-50 text-blue-800">{o.status_logistico || "—"}</span>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground mb-2">{o.tenant_name} · {o.network_display}</div>
                  <div className="space-y-1">
                    {(o.items || []).map((it, i) => (
                      <div key={i} className="text-xs flex items-center justify-between bg-neutral-50 rounded-sm px-2 py-1">
                        <span>
                          <span className="font-mono mr-2">{it.sku_variante}</span>
                          {it.nome}
                          {it.quantita > 1 && <span className="text-muted-foreground"> × {it.quantita}</span>}
                        </span>
                        <span>€ {(it.prezzo_unitario || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </AdminLayout>
  );
}

const Th = ({ children, center, right }) => (
  <th className={`px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground ${center ? "text-center" : right ? "text-right" : "text-left"}`}>{children}</th>
);

const Kpi = ({ label, value, highlight, testid }) => (
  <div className={`p-4 rounded-sm border ${highlight ? "border-foreground bg-foreground text-white" : "border-border bg-white"}`} data-testid={testid}>
    <div className={`overline ${highlight ? "text-white/70" : "text-muted-foreground"} mb-1`}>{label}</div>
    <div className="font-heading font-bold text-2xl">{value}</div>
  </div>
);
